import { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../utils/helper';

/**
 * Dashboard UI primitives for the Shoorah admin design system.
 *
 * The card recipe (from the Figma guideline, first shipped in People Plan):
 *   white surface · 1.5px shoorah-borderGray hairline · 8px radius ·
 *   borderless dark-mode variant · a circular grey badge holding a
 *   Streamline line icon.
 *
 * Build every dashboard out of these instead of bespoke divs — restyling the
 * panel then means editing this file and theme.js, nothing else.
 */

const CARD =
  'rounded-panel border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder';

export const IconBadge = ({ src, size = 'md', className = '' }) => (
  <div
    className={cn(
      'rounded-full border border-shoorah-borderGray dark:border-shoorah-darkBorder bg-shoorah-normanGray dark:bg-shoorah-darkBgColor flex items-center justify-center shrink-0',
      size === 'sm' ? 'w-10 h-10' : 'w-12 h-12',
      className
    )}
  >
    <img src={src} alt="" className={cn('dark:invert', size === 'sm' ? 'w-5 h-5' : 'w-6 h-6')} />
  </div>
);

IconBadge.propTypes = {
  src: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['sm', 'md']),
  className: PropTypes.string
};

/**
 * Metric tile: icon badge, label and value.
 *
 * `icon` is normally a Streamline src string, drawn inside the standard grey
 * IconBadge. It may also be a ready-made node — the legacy pages behind
 * DashboardInfoCard pass their own pre-coloured 56px SVG circles, and nesting
 * one of those inside a grey badge would put a circle in a circle. A node is
 * shown at the badge's footprint instead, so those pages get the standard card
 * surface and type today and can move to STAT_ICONS one at a time.
 *
 * `note` is a line under the figure ("+12% on last month"). Kept as a prop
 * rather than composed by callers so its position and colour stay the same on
 * every card that has one.
 */
export const StatCard = ({ icon, title, value, note = null, className = '' }) => (
  <div className={cn(CARD, 'h-full py-3 px-[19px]', className)}>
    <div className="flex items-center gap-4">
      {typeof icon === 'string' ? (
        <IconBadge src={icon} />
      ) : icon ? (
        <div className="w-12 h-12 shrink-0 flex items-center justify-center [&>svg]:w-12 [&>svg]:h-12 [&>img]:w-12 [&>img]:h-12">
          {icon}
        </div>
      ) : null}
      <div className="min-w-0">
        <p
          className="text-[13px] text-gray-500 dark:text-shoorah-darkBg truncate"
          // The hover tooltip only makes sense for text; a node would render
          // as "[object Object]".
          title={typeof title === 'string' ? title : undefined}
        >
          {title}
        </p>
        <p className="mt-0.5 text-[24px] leading-tight font-semibold text-shoorah-btn dark:text-white truncate">
          {value}
        </p>
        {note ? (
          <p
            className="mt-1 text-xs text-green-700 dark:text-green-400 truncate"
            title={typeof note === 'string' ? note : undefined}
          >
            {note}
          </p>
        ) : null}
      </div>
    </div>
  </div>
);

StatCard.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  title: PropTypes.node.isRequired,
  value: PropTypes.node,
  note: PropTypes.node,
  className: PropTypes.string
};

/**
 * Content card for charts, tables and lists. `h-full` plus a `flex-1` body
 * lets cards sharing a grid row line up on a common baseline.
 */
export const Panel = ({ title, subtitle, action, children, className = '' }) => (
  <div className={cn(CARD, 'h-full flex flex-col p-5 sm:p-6', className)}>
    {(title || action) && (
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          {title && (
            <h3 className="text-base font-medium text-gray-700 dark:text-gray-200">{title}</h3>
          )}
          {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    )}
    <div className={cn('flex-1', (title || action) && 'mt-4')}>{children}</div>
  </div>
);

Panel.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  action: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string
};

/** Colour-coded pill. Pass a tone's chip classes from theme.js (`toneChip`). */
export const Chip = ({ children, className = '' }) => (
  <span
    className={cn(
      'inline-block px-2.5 py-0.5 rounded-full text-xs font-medium',
      className || 'bg-gray-100 text-gray-700'
    )}
  >
    {children}
  </span>
);

Chip.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

/** Placeholder used inside a Panel when a chart or table has no rows yet. */
export const PanelEmpty = ({ children }) => (
  <p className="text-sm text-gray-400 dark:text-gray-500 py-10 text-center">{children}</p>
);

PanelEmpty.propTypes = {
  children: PropTypes.node
};

/** Card-styled table shell — hairline header rule, zebra-free rows. */
export const PanelTable = ({ head, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-shoorah-borderGray dark:border-shoorah-darkBorder">
          {head}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

PanelTable.propTypes = {
  head: PropTypes.node,
  children: PropTypes.node
};

/** Standard row class for PanelTable bodies. */
export const PANEL_ROW_CLASS =
  'border-b border-shoorah-borderGray/50 dark:border-shoorah-darkBorder/50 last:border-0 text-shoorah-lightBlack dark:text-shoorah-darkBg';

const initials = (name = '') =>
  name
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

/** Avatar that falls back to initials when there is no image, or it 404s. */
export const Avatar = ({ src, name, className = '' }) => {
  const [failed, setFailed] = useState(false);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name || ''}
        onError={() => setFailed(true)}
        className={cn('w-12 h-12 rounded-full object-cover shrink-0', className)}
      />
    );
  }

  return (
    <div
      className={cn(
        'w-12 h-12 rounded-full bg-shoorah-btn dark:bg-shoorah-darkBgColor flex items-center justify-center text-white text-base font-medium shrink-0',
        className
      )}
    >
      {initials(name)}
    </div>
  );
};

Avatar.propTypes = {
  src: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string
};

/**
 * Identity header for a detail page — the user or company a page is about.
 * Sits in its own Panel above the stat cards.
 */
export const EntityHeader = ({ image, name, subtitle, chips, action, className = '' }) => (
  <Panel className={className}>
    <div className="flex items-center gap-4">
      <Avatar src={image} name={name} />
      <div className="min-w-0">
        <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 truncate">{name}</h3>
        {subtitle && (
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
        )}
        {chips ? <div className="flex flex-wrap gap-1.5 mt-2">{chips}</div> : null}
      </div>
      {action ? <div className="ml-auto text-right shrink-0">{action}</div> : null}
    </div>
  </Panel>
);

EntityHeader.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string,
  subtitle: PropTypes.node,
  chips: PropTypes.node,
  action: PropTypes.node,
  className: PropTypes.string
};

/** "← Back to X" link that sits above a detail page's header. */
export const BackLink = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-2 text-sm font-medium text-shoorah-btn dark:text-shoorah-btnDark hover:underline"
  >
    <ArrowLeftIcon className="w-4 h-4" />
    {label}
  </button>
);

BackLink.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired
};

export default Panel;

/**
 * Period selector — the control that sits in a panel's header.
 *
 * The same pill appears on four cards in the design under three different
 * labels ("Duration / Weekly", "30 Days", "Jun 2026"), so it is one component
 * with the leading word optional rather than three.
 *
 * Deliberately a plain `<select>`. It carries at most a handful of fixed
 * periods, and a native control gets keyboard support, touch pickers and
 * screen-reader semantics that a custom dropdown would have to re-earn. The
 * chevron is drawn here because `appearance-none` removes the platform one.
 *
 * Pass it to a Panel's `action`, or to the chart cards' `action` prop.
 */
export const PeriodSelect = ({ label, value, onChange, options = [], className = '' }) => (
  <div className={cn('flex items-center gap-[15px]', className)}>
    {label && (
      <span className="text-xs font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg">
        {label}
      </span>
    )}
    <div className="relative">
      <select
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        className="appearance-none h-7 ps-4 pe-9 rounded-input border border-shoorah-borderGray dark:border-shoorah-darkBorder bg-transparent text-xs font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active cursor-pointer"
      >
        {options.map((option) => (
          <option key={option.value ?? option} value={option.value ?? option}>
            {option.label ?? option}
          </option>
        ))}
      </select>
      <svg
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
        className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 w-4 h-4 text-shoorah-gray3"
      >
        <path
          d="M4 6.5 8 10.5l4-4"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  </div>
);

PeriodSelect.propTypes = {
  /** The word before the control ("Duration"). Omit for a bare pill. */
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  /** Strings, or `{ label, value }` when the two differ. */
  options: PropTypes.array,
  className: PropTypes.string
};

/**
 * A panel of label/figure rows separated by hairlines.
 *
 * Six cards in the design are this, so it is the most repeated pattern after
 * the stat card — LastDrink and LastPuff are the same card twice, which is the
 * clearest sign it wanted to be a component.
 *
 * It is not a `PanelTable`: there are no columns to align across rows and no
 * header, just a figure per label. And it is not a row of `StatCard`s, because
 * these belong together as one subject — "everything we know about LastDrink"
 * — which is what the shared panel and title say.
 *
 * `meta` is the design's "Last updated" note. It sits top-right in small grey
 * type, and it is worth passing whenever the figures are cached or batched:
 * a number with no date on it gets read as live whether it is or not.
 */
export const StatListCard = ({ title, subtitle, rows = [], meta, action, className = '' }) => (
  <Panel
    title={title}
    subtitle={subtitle}
    action={
      action ||
      (meta ? (
        <p className="text-[10px] leading-[14px] text-end text-shoorah-gray3">{meta}</p>
      ) : null)
    }
    className={className}
  >
    {rows.length ? (
      <ul className="mt-6 flex flex-col">
        {rows.map((row) => (
          <li
            key={row.label}
            className="flex items-center justify-between gap-6 py-[7px] border-b border-shoorah-borderGray dark:border-shoorah-darkBorder last:border-0"
          >
            <span className="text-xs text-shoorah-lightBlack dark:text-shoorah-darkBg min-w-0 truncate">
              {row.label}
            </span>
            <span className="text-base text-shoorah-lightBlack dark:text-shoorah-darkBg tabular-nums whitespace-nowrap">
              {row.value}
            </span>
          </li>
        ))}
      </ul>
    ) : (
      <PanelEmpty>Nothing recorded yet.</PanelEmpty>
    )}
  </Panel>
);

StatListCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  /** `value` is pre-formatted — the row prints it as given, units and all. */
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  /** Small note top-right, e.g. "Last updated: 9 June 2026 at 11:37". */
  meta: PropTypes.node,
  /** Replaces `meta` when the header needs a control instead. */
  action: PropTypes.node,
  className: PropTypes.string
};

/**
 * A grid of bordered tiles inside one panel.
 *
 * The same figures as `StatListCard`, given more weight: a tile each and a
 * 24px figure rather than a row and a 16px one. Use it when the numbers are
 * the point of the card, and the list when they are supporting detail.
 *
 * The tiles are rounder than the panel holding them (12px inside 10px), which
 * is how the design draws it.
 */
export const StatTileGrid = ({ title, subtitle, tiles = [], columns = 2, action, className = '' }) => (
  <Panel title={title} subtitle={subtitle} action={action} className={className}>
    {tiles.length ? (
      <div
        className={cn(
          'mt-6 grid gap-[19px]',
          columns === 3 ? 'grid-cols-1 ssm:grid-cols-3' : 'grid-cols-1 ssm:grid-cols-2'
        )}
      >
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="rounded-[12px] border border-shoorah-borderGray dark:border-shoorah-darkBorder p-5 min-w-0"
          >
            <p className="text-sm text-shoorah-gray3 truncate" title={tile.label}>
              {tile.label}
            </p>
            <p className="mt-2 text-2xl font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg tabular-nums">
              {tile.value}
            </p>
          </div>
        ))}
      </div>
    ) : (
      <PanelEmpty>Nothing recorded yet.</PanelEmpty>
    )}
  </Panel>
);

StatTileGrid.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  tiles: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  columns: PropTypes.oneOf([2, 3]),
  action: PropTypes.node,
  className: PropTypes.string
};
